import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Label } from 'components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { Combobox } from 'components/ui/combobox';
import { Organization, getOrganizationById, addMemberToOrganization, removeMemberFromOrganization, regenerateOrganizationCode } from 'services/api/organizations';
import { AppUser, getAllUsers } from 'services/api/user';
import { ArrowLeft, MoreHorizontal, Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "components/ui/alert-dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu"

const OrganizationDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [allUsers, setAllUsers] = useState<AppUser[]>([]);
  const [members, setMembers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const [isAddMemberDialogOpen, setIsAddMemberDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [confirmingUserId, setConfirmingUserId] = useState<string | null>(null);
  
  // Pagination and sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState<'displayName' | 'email' | 'uid'>('displayName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Sorting and pagination logic
  const sortedMembers = [...members].sort((a, b) => {
    const aValue = a[sortField] || '';
    const bValue = b[sortField] || '';
    
    if (sortDirection === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  const totalPages = Math.ceil(sortedMembers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMembers = sortedMembers.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field: 'displayName' | 'email' | 'uid') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Code copied to clipboard!");
  };

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [orgData, usersData] = await Promise.all([
        getOrganizationById(id),
        getAllUsers(),
      ]);

      setOrganization(orgData);
      setAllUsers(usersData);

      // Filter user objects who are members of this organization
      const memberDetails = usersData.filter(user => orgData.members.includes(user.uid));
      setMembers(memberDetails);

    } catch (err) {
      toast.error('Failed to load organization details.');
      setError('Failed to load organization details.'); // Keep for critical failure
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handleRegenerateCode = async () => {
    if (!id) return;
    try {
      setIsRegenerating(true);
      const updatedOrg = await regenerateOrganizationCode(id);
      setOrganization(updatedOrg);
      toast.success("New organization code generated!");
    } catch (err) {
      toast.error("Failed to regenerate code.");
      console.error(err);
    } finally {
      setIsRegenerating(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddMember = async () => {
    if (!id || !selectedUserId) return;
    try {
        setIsAddingMember(true);
        await addMemberToOrganization(id, selectedUserId);
        toast.success("Member added successfully!");
        setIsAddMemberDialogOpen(false);
        setSelectedUserId('');
        // Refresh data to show the new member
        fetchData(); 
    } catch(err: any) {
        const errorMessage = err.message || 'Failed to add member.';
        toast.error(errorMessage);
        console.error('Add member error:', err);
    } finally {
        setIsAddingMember(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!id) return;
    try {
      setRemovingUserId(userId);
      await removeMemberFromOrganization(id, userId);
      toast.success("Member removed successfully.");
      fetchData(); // Refresh data
    } catch (err) {
      toast.error("Failed to remove member.");
      console.error(err);
    } finally {
      setRemovingUserId(null);
      setConfirmingUserId(null);
    }
  };

  if (loading) return (
    <div className="space-y-6 pt-2">
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
      <div className="h-40 bg-gray-200 rounded animate-pulse"></div>
      <div className="h-28 bg-gray-200 rounded animate-pulse"></div>
      <div className="h-96 bg-gray-200 rounded animate-pulse"></div>
    </div>
  );
  if (error) return <div className="text-red-500">{error}</div>; // Keep for critical data failure
  if (!organization) return <div>Organization not found.</div>;

  // Filter out admin users and users who are already members
  const nonMemberUsers = allUsers.filter(user => 
    !organization.members.includes(user.uid) && 
    user.role !== 'admin'
  );
  
  const userOptions = nonMemberUsers.map(user => ({
    value: user.uid,
    label: `${user.displayName || user.email} (${user.uid.substring(0, 5)})`
  }));

  return (
    <div className="p-8 pt-2">
      <Link to="/admin/organizations" className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Organizations
      </Link>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>{organization.name}</CardTitle>
          <CardDescription>ID: {organization.id}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {organization.address && (
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Address</Label>
                <p className="text-sm">{organization.address}</p>
              </div>
            )}
            {organization.contact && (
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Contact</Label>
                <p className="text-sm">{organization.contact}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
            <CardTitle>Organization Code</CardTitle>
            <CardDescription>Use this code to invite new users to the organization.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-between">
            <div className="flex items-center gap-4">
                <span className="font-mono text-lg bg-gray-100 p-2 rounded-md">{organization.code || 'N/A'}</span>
                {organization.code && (
                    <Button variant="ghost" size="icon" onClick={() => copyToClipboard(organization.code!)}>
                        <Copy className="h-4 w-4" />
                    </Button>
                )}
            </div>
            <div className="text-right">
                <p className="text-sm text-gray-500">Expires at: {formatDate(organization.codeExpiresAt)}</p>
                <Button onClick={handleRegenerateCode} size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-700" disabled={isRegenerating}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${isRegenerating ? 'animate-spin' : ''}`} />
                    {isRegenerating ? 'Regenerating code...' : 'Regenerate Code'}
                </Button>
            </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
                <CardTitle>Members</CardTitle>
                <CardDescription>Users who belong to this organization.</CardDescription>
            </div>
                          <Dialog open={isAddMemberDialogOpen} onOpenChange={setIsAddMemberDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700" 
                  disabled={nonMemberUsers.length === 0}
                  title={nonMemberUsers.length === 0 ? "No users available to add (all users are either members or admins)" : "Add a new member to this organization"}
                >
                  Add Member
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add a new member</DialogTitle>
                  <DialogDescription>Select a user to add to this organization.</DialogDescription>
                </DialogHeader>
                <div className="py-4">
                    {nonMemberUsers.length === 0 ? (
                      <div className="text-center py-4 text-muted-foreground">
                        <p>No users available to add.</p>
                        <p className="text-sm">All users are either already members or have admin role.</p>
                      </div>
                    ) : (
                      <Combobox
                          options={userOptions}
                          value={selectedUserId}
                          onChange={setSelectedUserId}
                          placeholder="Select a user..."
                          searchPlaceholder="Search for a user..."
                          emptyText="No users found or all users are members."
                      />
                    )}
                </div>
                <DialogFooter>
                    <Button onClick={handleAddMember} disabled={!selectedUserId || isAddingMember} className="bg-emerald-600 hover:bg-emerald-700">
                      {isAddingMember ? (
                        <>
                          <span className="mr-2 inline-block animate-spin">⏳</span>
                          Adding...
                        </>
                      ) : (
                        'Add Member'
                      )}
                    </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('displayName')}
                >
                  <div className="flex items-center gap-2">
                    Name
                    {sortField === 'displayName' && (
                      <span className="text-emerald-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center gap-2">
                    Email
                    {sortField === 'email' && (
                      <span className="text-emerald-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('uid')}
                >
                  <div className="flex items-center gap-2">
                    User ID
                    {sortField === 'uid' && (
                      <span className="text-emerald-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedMembers.map(member => (
                <TableRow key={member.uid}>
                  <TableCell>{member.displayName || 'N/A'}</TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>{member.uid}</TableCell>
                  <TableCell className="text-right">
                    <AlertDialog
                      open={confirmingUserId === member.uid}
                      onOpenChange={(open) => setConfirmingUserId(open ? member.uid : null)}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem className="text-red-600" onClick={() => setConfirmingUserId(member.uid)}>Remove</DropdownMenuItem>
                          </AlertDialogTrigger>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will remove the user from the organization.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel disabled={removingUserId === member.uid}>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); handleRemoveMember(member.uid); }}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            disabled={removingUserId === member.uid}
                          >
                            {removingUserId === member.uid ? (
                              <>
                                <span className="mr-2 inline-block animate-spin">⏳</span>
                                Removing...
                              </>
                            ) : (
                              'Continue'
                            )}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-2 py-4">
              <div className="flex items-center gap-4">
                <div className="text-sm text-muted-foreground">
                  Da {startIndex + 1} a {Math.min(startIndex + itemsPerPage, sortedMembers.length)} di {sortedMembers.length} utenti
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="items-per-page" className="text-sm">Items per page:</Label>
                  <select
                    id="items-per-page"
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    className="border rounded px-2 py-1 text-sm"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Precedente
                </Button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      className={currentPage === page ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                    >
                      {page}
                    </Button>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Successivo
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrganizationDetailsPage; 